// ===================== NEONFANG — bootstrap & app state machine =====================
import { Camera } from './engine/camera.js';
import { Input } from './engine/input.js';
import { Audio } from './engine/audio.js';
import { Save } from './engine/save.js';
import { Settings } from './engine/settings.js';
import { loadSprites, spritesReady } from './content/sprites.js';
import { Game } from './core/game.js';
import { HUD } from './core/hud.js';
import { Screens } from './core/screens.js';
import { renderTitleScene } from './core/titlescene.js';
import { Tutorial } from './core/tutorial.js';
import { Platform } from './engine/platform.js';
import { rollUpgrades, PASSIVES, availableEvolution } from './content/upgrades.js';
import { getWeapon, CharacterDefs } from './core/registries.js';
import { metaBonuses } from './content/meta.js';
import { ACH_BY_ID, nextHunts } from './content/achievements.js';
import {
  MAP_NODES, START_NODE, nodeById, nodeState, runConfigFor, worldMapBonuses,
  mapProgress, regionById, tickReclamation, reclaimBounty, msToNextSiege,
  threatBounty, threatLabel, threatCeiling, MAX_THREAT,
  ANOMALIES, anomaliesForToday, anomalyFor, msToAnomalyReset, utcDay,
  isoWeek, gauntletWeekKey, gauntletSeed, gauntletUnlocked, gauntletConfigFor,
  GAUNTLET, formatSeed, gauntletScore,
} from './content/worldmap.js';

// Register all content (side-effect imports populate the registries).
import './content/elements.js';
import './content/weapons.js';
import './content/evolutions.js';
import { ULTRA_IDS } from './content/ultraweapons.js';
import './content/enemies.js';
import './content/bosses.js';
import './content/characters.js';

const passiveById = Object.fromEntries(PASSIVES.map((p) => [p.id, p]));

const canvas = document.getElementById('game');
const ctx = canvas.getContext('2d');
const camera = new Camera();
const input = new Input(canvas);
const game = new Game(canvas, camera, input);
const hud = new HUD();
const screens = new Screens();
const tutorial = new Tutorial();

// ------------------------------------------------------------- achievements
function tryAchieve(id) {
  const def = ACH_BY_ID[id];
  if (!def) return false;
  if (!Save.achieve(id)) return false;      // already had it
  if (def.coins) Save.addCoins(def.coins);
  showToast(def);
  Audio.chest();
  return true;
}
// Toasts queue instead of stacking: at most TOAST_MAX are on screen, the rest
// follow as earlier ones fade. Ending a run right after a burst of unlocks used
// to pile five toasts over the results headline.
let toastWrap = null;
const TOAST_MAX = 3;
const toastQueue = [];
let toastsLive = 0;
function pushToast(icon, html, cls = '') {
  toastQueue.push({ icon, html, cls });
  pumpToasts();
}
function pumpToasts() {
  if (!toastWrap) toastWrap = document.getElementById('toast-wrap');
  if (!toastWrap) return;
  while (toastsLive < TOAST_MAX && toastQueue.length) {
    const { icon, html, cls } = toastQueue.shift();
    const el = document.createElement('div');
    el.className = 'toast' + (cls ? ' ' + cls : '');
    el.innerHTML = `<span class="toast-icon">${icon}</span><span>${html}</span>`;
    toastWrap.appendChild(el);
    toastsLive++;
    requestAnimationFrame(() => el.classList.add('show'));
    setTimeout(() => {
      el.classList.remove('show');
      setTimeout(() => { el.remove(); toastsLive--; pumpToasts(); }, 400);
    }, 3600);
  }
}
function showToast(def) {
  pushToast(def.icon, `<b>Achievement unlocked</b><br>${def.name}` +
    `${def.coins ? ` &nbsp;<span style="color:var(--gold)">◈+${def.coins}</span>` : ''}`);
}
game.onAchievement = (id) => tryAchieve(id);

// OVERRUN — the finale is beaten and the win banked; offer to stay for the
// endless phase. The engine froze the sim ('overrunoffer') before calling.
game.onOfferOverrun = () => {
  state = 'overrunoffer';
  screens.overrunOffer({
    bestSeconds: game.node && game.node.nodeId ? Save.overrunBest(game.node.nodeId) : 0,
    onStay: () => { screens.hide(); game.beginOverrun(); state = 'playing'; },
    onBank: () => { screens.hide(); game.bankWin(); },
  });
};

// Bestiary discovery: the sim reports every kill's defId key; first sighting
// of each foe is banked. The Set makes the per-kill path allocation-free —
// storage is only touched on an actual discovery (a handful per save).
const seenCache = new Set(Save.data.seen);
game.onDiscover = (key) => {
  if (seenCache.has(key)) return;
  seenCache.add(key);
  if (!Save.data.seen.includes(key)) { Save.data.seen.push(key); Save.flush(); }
};

let state = 'loading';
let currentMusic = null;

// -------------------------------------------------------------- canvas sizing
// ---- adaptive resolution governor ----
// Render-only, sim-untouched: when the rolling frame time says the device
// can't hold the budget, the canvas steps down through DPR tiers (resolution
// is the dominant render cost); when there's comfortable headroom it steps
// back up. A phone that can't hold 60 used to just run slow-motion via the
// backlog-dropping stepper, which reads as "the game is broken".
const DPR_STEPS = [2, 1.5, 1.25, 1];
let dprStep = 0;
let govAcc = 0, govN = 0, govCooldown = 0;
function governFrame(rawDt) {
  if (rawDt > 0.1) { govAcc = 0; govN = 0; return; }  // stall/tab-switch — not a sample
  govAcc += rawDt; govN++;
  govCooldown -= rawDt;
  if (govN < 90 || govCooldown > 0) return;
  const avg = govAcc / govN;
  govAcc = 0; govN = 0;
  if (avg > 0.022 && dprStep < DPR_STEPS.length - 1) { dprStep++; govCooldown = 3; resize(); }
  else if (avg < 0.014 && dprStep > 0) { dprStep--; govCooldown = 5; resize(); }
}

function resize() {
  const dpr = Math.min(window.devicePixelRatio || 1, DPR_STEPS[dprStep]);
  const w = window.innerWidth, h = window.innerHeight;
  canvas.width = Math.max(1, Math.floor(w * dpr));
  canvas.height = Math.max(1, Math.floor(h * dpr));
  canvas.style.width = w + 'px';
  canvas.style.height = h + 'px';
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  camera.resize(w, h);
}
window.addEventListener('resize', resize);
window.addEventListener('orientationchange', resize);

// Resume audio on interaction (browser autoplay policy) — and keep resuming.
// These listeners are deliberately NOT once-only: an OS audio-session
// interruption (a phone call, Siri, another app taking audio) suspends the
// context long after the first unlock, and without a live re-resume path the
// game stays silent until a reload. Audio.resume() is a no-op when running.
const kickAudio = () => { Audio.resume(); };
window.addEventListener('pointerdown', kickAudio);
window.addEventListener('keydown', kickAudio);

// Auto-pause when the tab/window loses focus; recover audio when it returns.
// A hidden page also hard-suspends the AudioContext — minimising the app (or
// a rewarded ad covering it) must actually silence the game, not just leave
// the music running behind the OS.
function autoPause() { if (state === 'playing') togglePause(); }
document.addEventListener('visibilitychange', () => {
  if (document.hidden) { autoPause(); Audio.suspend(); }
  else Audio.resume();
});
window.addEventListener('blur', autoPause);

// ------------------------------------------------------------------- music
const inRun = () => state === 'playing' || state === 'levelup' || state === 'paused' || state === 'chest' || state === 'reviveoffer' || state === 'overrunoffer';
function updateMusic() {
  let key = 'menu';
  if (inRun()) key = game.stageId || 'meadow';
  if (key !== currentMusic) { currentMusic = key; Audio.startMusic(key); }

  // Dynamic layers: pressure from horde size, combo heat and boss presence.
  if (inRun() && game.player) {
    const horde = Math.min(1, game.enemies.count / 120);
    const heat = Math.min(game.combo, 80) / 80;
    const progress = game.duration ? Math.min(1, game.elapsed / game.duration) : 0;
    const boss = game.boss && !game.boss.dead;
    Audio.setIntensity(Math.min(1, 0.22 + horde * 0.45 + heat * 0.2 + progress * 0.15 + (boss ? 0.45 : 0)));
    Audio.setBossMode(!!boss);
  } else {
    Audio.setIntensity(0.25);
    Audio.setBossMode(false);
  }
  // Muffle the mix whenever the action is frozen (pause, level-up, chest).
  Audio.setPaused(inRun() && state !== 'playing');
}

// -------------------------------------------------------------- state helpers
function goTitle() {
  state = 'title';
  hud.hide();
  game.state = 'menu';
  updateMusic();
  screens.title({
    onPlay: () => goSelect(),
    onShop: () => screens.skillTree({ onBack: goTitle, onAscended: () => tryAchieve('ascension') }),
    onAchievements: () => screens.achievements({ onBack: goTitle }),
    onSettings: () => screens.settings({ onBack: goTitle, toast: pushToast }),
    onReset: () => { Save.reset(); seenCache.clear(); goTitle(); },
  });
}

let chosenChar = null;
let chosenNode = START_NODE;
let chosenTier = 0;

function goSelect() {
  state = 'select';
  screens.characterSelect({
    onPick: (def) => { chosenChar = def; goWorldMap(); },
    onBack: () => goTitle(),
  });
}

// Advance the reclamation clock and tell the player what it cost them.
function runReclamation() {
  for (const lost of tickReclamation()) {
    pushToast('⚔', `<b>Territory lost</b><br>${lost.name} has been retaken`, 'danger');
  }
}

function goWorldMap(selectedId) {
  state = 'select';
  hud.hide();
  game.state = 'menu';
  updateMusic();
  runReclamation();
  screens.worldMap({
    onPick: (node, tier) => startRun(chosenChar, node, tier),
    onGauntlet: () => startGauntlet(),
    onBack: () => goSelect(),
  }, selectedId || (chosenNode && chosenNode.id));
}

// Fang Tree bonuses plus the blessings of every shrine hex you hold.
function runBonuses() {
  const b = metaBonuses();
  for (const [k, v] of Object.entries(worldMapBonuses())) b[k] = (b[k] || 0) + v;
  return b;
}

function startRun(charDef, node, tier) {
  screens.hide();
  hud.show();
  chosenChar = charDef;
  chosenNode = node || chosenNode || START_NODE;
  chosenTier = tier === undefined ? chosenTier : tier;
  game.start(charDef, runBonuses(), chosenNode.stageId, runConfigFor(chosenNode, chosenTier));
  state = 'playing';
  currentMusic = null;
  updateMusic();
  tutorial.start(game);
  biteTransition();
}

// THE WEEKLY GAUNTLET — one attempt per ISO week against the same seeded
// horde as every other player. The attempt is consumed AT RUN START (and
// flushed) so quitting out never refunds it; a second start the same week is
// refused before the confirm even shows.
function startGauntlet() {
  if (!gauntletUnlocked()) { Audio.hurt(); return false; }
  const cfg = gauntletConfigFor();
  if (Save.gauntletAttempted(cfg.gauntletWeek)) {
    Audio.hurt();
    pushToast('⚔', '<b>The Gauntlet is spent</b><br>One attempt a week — a new horde rises on Monday.', 'danger');
    return false;
  }
  // Branded confirm (native confirm() is unstylable and jarring on mobile).
  // Returning true means the offer was shown; the run starts from its OK.
  screens.confirmDialog({
    title: '⚔ WEEKLY GAUNTLET',
    body: 'One attempt this week — spent the moment the run starts.<br>Every hunter in the world faces this same seeded horde.',
    okLabel: '⚔ BEGIN',
    cancelLabel: '◄ Not yet',
    onOk: () => {
      Save.startGauntletAttempt(cfg.gauntletWeek);
      screens.hide();
      hud.show();
      game.start(chosenChar || CharacterDefs.get('wanderer'), runBonuses(), cfg.stageId, cfg);
      state = 'playing';
      currentMusic = null;
      updateMusic();
      tutorial.start(game);
      biteTransition();
    },
    onCancel: () => goWorldMap(),
  });
  return true;
}

// Branded run-start flourish: the NEONFANG jaws snap shut over the screen.
let biteTimer = 0;
function biteTransition() {
  if (Settings.reducedMotion) return;
  const el = document.getElementById('bite');
  if (!el) return;
  clearTimeout(biteTimer);
  el.classList.remove('hidden', 'chomp');
  void el.offsetWidth;               // restart the CSS animation cleanly
  el.classList.add('chomp');
  setTimeout(() => { game.camera.shake(9, 0.22); Audio.hit(); }, 320);
  biteTimer = setTimeout(() => { el.classList.add('hidden'); el.classList.remove('chomp'); }, 950);
}

// Rewarded-ad revive: the sim freezes on death and offers one second chance.
// Only wired up when a rewarded ad can actually be shown (native builds).
if (Platform.isNative) {
  game.onOfferRevive = () => {
    if (!Platform.ads.available) { game.finishDeath(); return; }
    state = 'reviveoffer';
    screens.reviveOffer({
      onWatch: async () => {
        const { rewarded } = await Platform.ads.showRewarded('revive');
        screens.hide();
        if (rewarded) { game.adRevive(); state = 'playing'; }
        else game.finishDeath();
      },
      onDecline: () => { screens.hide(); game.finishDeath(); },
    });
  };
}

// Level-up: pause the sim, present cards (+ reroll/banish/skip), apply, repeat.
function openLevelUp() {
  state = 'levelup';
  Audio.levelup();
  game.addFlash('#7df9ff', 0.3);
  presentLevelUp();
}
function presentLevelUp() {
  const cards = rollUpgrades(game, 3, { banished: game.banished });
  screens.levelUp({
    level: game.player.level,
    cards,
    rerolls: game.rerolls,
    banishes: game.banishes,
    onChoose: (card) => {
      card.apply(game);
      game.pendingLevels = Math.max(0, game.pendingLevels - 1);
      tutorial.notifyLevelUp();
      Platform.haptic('medium');
      screens.hide();
      if (game.pendingLevels > 0) openLevelUp();
      else state = 'playing';
    },
    onReroll: () => { if (game.rerolls > 0) { game.rerolls--; screens.hide(); presentLevelUp(); } },
    onBanish: (card) => {
      if (game.banishes <= 0) return;
      game.banishes--;
      game.banished.add(card.key);
      screens.hide();
      presentLevelUp();
    },
    onSkip: () => {
      game.pendingLevels = Math.max(0, game.pendingLevels - 1);
      game.player.heal(game.player.maxHp * 0.1);
      game.gold += 3;
      screens.hide();
      if (game.pendingLevels > 0) openLevelUp();
      else state = 'playing';
    },
  });
}

// Treasure chest: evolves a ready weapon (VS-style) or grants a bonus upgrade.
function openChest() {
  game.pendingChests = Math.max(0, (game.pendingChests || 0) - 1);
  const evo = availableEvolution(game.player);
  if (evo) {
    state = 'chest';
    game.player.evolveWeapon(evo.baseId, evo.into, getWeapon);
    Platform.haptic('medium');
    tryAchieve('evolved');
    game.addFlash('#ff8a3d', 0.6);
    game.camera.shake(9, 0.35);
    Audio.multiply();
    screens.chest({
      evolved: evo.def,
      onClose: () => {
        screens.hide();
        if (game.pendingChests > 0) openChest();
        else state = 'playing';
      },
    });
  } else {
    // No evolution ready → chests still reward you with a free upgrade pick.
    game.pendingLevels += 1;
  }
}

// Ultraweapon reveal — an Alpha super boss died and left its trophy.
function openUltra() {
  const grant = game.pendingUltra;
  game.pendingUltra = null;
  if (!grant) return;
  state = 'chest';
  tryAchieve('ultra');
  // Collection is tracked across runs for the Apex Arsenal achievement.
  if (!Save.data.seen.includes('ultra:' + grant.def.id)) {
    Save.data.seen.push('ultra:' + grant.def.id);
    Save.flush();
  }
  if (ULTRA_IDS.every((id) => Save.data.seen.includes('ultra:' + id))) tryAchieve('ultra_all');
  Platform.haptic('heavy');
  game.addFlash('#ffd15c', 0.6);
  game.camera.shake(10, 0.4);
  Audio.multiply();
  screens.chest({
    evolved: grant.def,
    heading: '☠ ALPHA TROPHY ☠',
    tag: grant.isNew ? 'ULTRAWEAPON UNLEASHED' : 'ULTRAWEAPON EMPOWERED',
    onClose: () => { screens.hide(); state = 'playing'; },
  });
}

function buildInfo() {
  const p = game.player;
  const info = p.weapons.map((w) => ({ icon: w.def.icon, lvl: w.level }));
  // Passive levels, except Multiplication, which reports its combined ×
  // (cards plus golden pickups) exactly as the HUD does.
  for (const s of p.passiveSlots()) {
    const pa = passiveById[s.id];
    info.push({ icon: pa ? pa.icon : '◆', lvl: s.label, cls: s.id === 'multiply' ? 'tag-multiply' : '' });
  }
  return info;
}

// Damage dealt per weapon this run (top entries with share of total).
function damageInfo() {
  const entries = Object.entries(game.damageByWeapon || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  return entries.slice(0, 6).map(([id, v]) => {
    const def = getWeapon(id);
    return { icon: def ? def.icon : '◆', name: def ? def.name : id, dmg: v, pct: total ? v / total : 0 };
  });
}

// Effective player stats for the pause panel.
function playerStats() {
  const p = game.player;
  return [
    ['Damage', '×' + p.damageMult.toFixed(2)],
    ['Attack speed', '×' + (1 / p.cooldownMult).toFixed(2)],
    ['Multiplication', '×' + p.projMult.toFixed(2) + (p.projAdd ? ' +' + p.projAdd : '')],
    ['Area', '×' + p.areaMult.toFixed(2)],
    ['Move speed', String(Math.round(p.moveSpeed))],
    ['Crit', Math.round(p.critChance * 100) + '% ×' + p.critMult.toFixed(1)],
    ['Armor', String(Math.round(p.armor))],
    ['Regen', p.regen.toFixed(1) + '/s'],
    ['Luck', p.luck.toFixed(1)],
    ['Gold gain', '×' + p.goldMult.toFixed(2)],
    ['XP gain', '×' + p.xpMult.toFixed(2)],
    ['Curse', String(p.curse)],
    ['Seed', formatSeed(game.seed)],
  ];
}

function togglePause() {
  if (state === 'playing') {
    state = 'paused';
    screens.pause(pauseArgs());
  } else if (state === 'paused') {
    screens.hide();
    state = 'playing';
  }
}
function pauseArgs() {
  return {
    buildInfo: buildInfo(),
    damageInfo: damageInfo(),
    stats: playerStats(),
    onResume: () => { screens.hide(); state = 'playing'; },
    onSettings: () => screens.settings({ onBack: () => screens.pause(pauseArgs()), toast: pushToast }),
    onMenu: () => { screens.hide(); goTitle(); },
  };
}

// End of run (death or survived) → banked coins, unlocks, summary.
game.onGameOver = (summary) => {
  state = 'dead';
  const node = summary.nodeId ? nodeById(summary.nodeId) : null;
  const tier = summary.threat || 0;
  const unlocks = [];

  // A Gauntlet run banks its outcome first: a clear pays the flat bounty (on
  // top of in-run coins), a death pays only what was picked up. There is no
  // hex to conquer — the node blocks below simply never fire (nodeId is null).
  let bounty = 0;
  let gScore = null, gScoreBest = false, gShareText = null;
  if (summary.gauntlet) {
    gScore = gauntletScore(summary);
    const res = Save.recordGauntletResult(summary.gauntletWeek, summary.seconds, summary.won, gScore);
    gScoreBest = res.newBestScore;
    // Wordle-style copyable result — everyone fights the same seeded horde,
    // so the seed + score line IS the comparison, no backend involved.
    const mm = Math.floor(summary.seconds / 60), ss = String(Math.floor(summary.seconds % 60)).padStart(2, '0');
    gShareText = `NEONFANG — WEEKLY GAUNTLET ${formatSeed(summary.seed)}\n`
      + `🏆 ${gScore.toLocaleString('en-US')} pts · ${summary.kills} kills · `
      + (summary.won ? 'SURVIVED ✓' : `fell at ${mm}:${ss}`);
    if (summary.won) {
      bounty = GAUNTLET.bounty;
      unlocks.push('The Weekly Gauntlet — survived');
      tryAchieve('gauntlet_first');
      if (res.clears >= 5) tryAchieve('gauntlet_five');
    }
  }

  // Surviving a hex takes it — a first clear conquers it outright, and winning
  // on contested ground drives the horde back off land you already owned.
  if (summary.won && node && Save.isContested(node.id)) {
    Save.reclaim(node.id);
    bounty = reclaimBounty(node);
    unlocks.push(node.name + ' reclaimed');
    if (node.boonText) unlocks.push('Blessing restored — ' + node.boonText);
    tryAchieve('reclaimer');
  } else if (summary.won && node && Save.conquer(node.id)) {
    bounty = node.type.bounty;
    Save.unlockStage(node.stageId);       // keeps the legacy stage list in step
    if (node.boonText) unlocks.push('Blessing — ' + node.boonText);
    if (node.isLair) unlocks.push(regionById(node.regionId).name + ' has fallen');
    if (node.isBridge) tryAchieve('pathfinder');
    if (node.isLair) tryAchieve('clear_' + node.regionId);
  }
  // Threat mastery pays on top, the first time you beat a tier on this hex.
  if (summary.won && node && tier > 0) {
    const record = Save.recordThreat(node.id, tier);
    if (record) {
      bounty += threatBounty(node, tier);
      unlocks.push(`Threat ${threatLabel(tier)} — ${node.name}`);
    }
    if (tier >= 5) tryAchieve('ordeal');
    if (tier >= MAX_THREAT) tryAchieve('nightmare');
    if (record && threatCeiling() > tier) {
      pushToast('☠', `<b>Threat ${threatLabel(tier + 1)} unlocked</b><br>Every hex can be pushed further`);
    }
  }
  if (summary.won && mapProgress().done >= MAP_NODES.length) tryAchieve('warlord');

  // OVERRUN depth: any overrun run (they all end as wins) can set the hex's
  // best survival-past-the-finale record.
  let overrunBest = false;
  if (summary.overrun && node) {
    overrunBest = Save.recordOverrun(node.id, summary.overrunSeconds);
  }

  // The Gauntlet's clear payment is its flat bounty + in-run coins, nothing
  // else — the ordinary per-run clear bonus stays out of it.
  const bonus = summary.won && !summary.gauntlet ? 50 + Math.floor(summary.seconds / 10) : 0;
  const coinsEarned = summary.gold + bonus + bounty;
  // "★ NEW BEST TIME ★" only when there was a previous best to beat and the
  // run is non-trivial — a 3-second first death used to earn the banner.
  const prevBest = Save.data.bestTime || 0;
  const wasBest = prevBest > 0 && summary.seconds > prevBest && summary.seconds >= 60;
  Save.recordRun({ character: summary.character, seconds: summary.seconds, kills: summary.kills, coins: coinsEarned });

  const tryUnlock = (id, label) => { if (!Save.isUnlocked(id) && Save.unlock(id)) unlocks.push(label); };
  if (summary.seconds >= 180) tryUnlock('pyre', 'Pyre');
  if (summary.seconds >= 360) tryUnlock('frost', 'Frost');
  if (Save.data.totalKills >= 1500) tryUnlock('storm', 'Storm');
  if (summary.seconds >= 600) tryUnlock('wisp', 'Verdant');

  // Run-end achievements (live ones fired during the run).
  if (summary.kills >= 100) tryAchieve('centurion');
  if (summary.kills >= 1000) tryAchieve('slayer');
  if (summary.kills >= 5000) tryAchieve('annihilator');
  if (Save.data.totalKills >= 10000) tryAchieve('legion');
  if (summary.seconds >= 300) tryAchieve('survive5');
  if (summary.seconds >= 600) tryAchieve('survive10');
  if (Save.data.coins >= 500) tryAchieve('rich');

  screens.gameOver({
    summary, coinsEarned, bonus, bounty, unlocks, isBest: wasBest,
    score: gScore, scoreBest: gScoreBest, shareText: gShareText, overrunBest,
    bountyLabel: summary.gauntlet ? 'Gauntlet Bounty' : undefined,
    canDouble: Platform.ads.available && coinsEarned > 0,
    onDouble: async () => {
      const { rewarded } = await Platform.ads.showRewarded('double_coins');
      if (rewarded) { Save.addCoins(coinsEarned); screens.markDoubled(coinsEarned * 2); }
    },
    // The Gauntlet has no retry — the week's attempt is spent — so its
    // primary action leads back to the map instead.
    retryLabel: summary.gauntlet ? '🗺 World Map' : undefined,
    onRetry: summary.gauntlet
      ? () => goWorldMap()
      : () => startRun(game.player.char, node || chosenNode, tier),
    onMap: node ? () => goWorldMap(node.id) : null,
    onMenu: () => goTitle(),
  });
};

// --------------------------------------------------- gamepad menu navigation
// D-pad / stick-flick moves a focus ring over the open overlay's cards and
// buttons; A activates, B backs out. Keyboard/mouse users are unaffected.
function gamepadMenuNav() {
  if (!screens.isOpen) return;
  const wantsNav = input.pressed('gp_left') || input.pressed('gp_right') ||
    input.pressed('gp_up') || input.pressed('gp_down') ||
    input.pressed('gp_a') || input.pressed('gp_b');
  if (!wantsNav) return;
  const focusables = [...document.querySelectorAll('#overlay .card, #overlay .hexnode, #overlay .btn:not(:disabled)')];
  if (!focusables.length) return;
  let idx = focusables.findIndex((el) => el.classList.contains('gp-focus'));
  const move = (d) => {
    if (idx >= 0) focusables[idx].classList.remove('gp-focus');
    idx = idx < 0 ? (d > 0 ? 0 : focusables.length - 1) : (idx + d + focusables.length) % focusables.length;
    focusables[idx].classList.add('gp-focus');
    focusables[idx].scrollIntoView({ block: 'nearest' });
  };
  if (input.pressed('gp_right') || input.pressed('gp_down')) move(1);
  else if (input.pressed('gp_left') || input.pressed('gp_up')) move(-1);
  else if (input.pressed('gp_a')) {
    const target = idx >= 0 ? focusables[idx]
      : (document.querySelector('#overlay .btn.primary') || focusables[0]);
    target.click();
  } else if (input.pressed('gp_b')) {
    const back = document.querySelector('#overlay #btn-back');
    if (back) back.click();
  }
}

// Gamepad courtesy: confirm the pad the first time it speaks (browsers only
// surface a pad after its first input), and pause when it drops mid-run — a
// dead battery shouldn't cost a build.
let gpGreeted = false;
input.onGamepadConnect = () => {
  if (gpGreeted) return;
  gpGreeted = true;
  pushToast('🎮', '<b>Gamepad connected</b><br>Stick/d-pad moves · Start pauses · A selects · B backs out');
};
input.onGamepadDisconnect = () => {
  if (state === 'playing') {
    togglePause();
    pushToast('🎮', '<b>Gamepad disconnected</b><br>Game paused', 'danger');
  }
};

// Debug/testing hook (harmless in production; used by automated smoke tests).
window.__neon = {
  game, screens, Save,
  map: {
    MAP_NODES, nodeById, nodeState, mapProgress, regionById, START_NODE,
    tickReclamation, msToNextSiege, runConfigFor, threatCeiling, threatLabel,
    ANOMALIES, anomaliesForToday, anomalyFor, msToAnomalyReset, utcDay,
    isoWeek, gauntletWeekKey, gauntletSeed, gauntletUnlocked, gauntletConfigFor,
    GAUNTLET, formatSeed,
  },
  get state() { return state; },
  nextHunts: (n) => nextHunts((id) => Save.isAchieved(id), n),
  goWorldMap: (id) => goWorldMap(id),
  startNode: (id) => startRun(chosenChar || CharacterDefs.get('wanderer'), nodeById(id)),
  startGauntlet: () => startGauntlet(),
  openLevelUp: () => openLevelUp(),
  forceBoss: () => game.forceBoss(),
  forceAlpha: () => { if (!game.boss) game._spawnBoss(game.stage.bosses[0], { alpha: true }); },
};

// -------------------------------------------------------------------- loop
const STEP = 1 / 60;
let last = performance.now();
let acc = 0;
let menuT = 0;      // title-scene animation clock
let crashed = false;

function frame(now) {
  if (crashed) return;
  try {
    let dt = (now - last) / 1000;
    last = now;
    if (state === 'playing') governFrame(dt);   // raw delta — measures the device
    if (dt > 0.25) dt = 0.25; // long stall (backgrounded) — don't fast-forward the sim

    input.update();

    if (input.pressed('escape') || input.pressed('p') || input.pressed('gp_start')) {
      if (state === 'playing' || state === 'paused') togglePause();
    }
    if (input.pressed('m')) { Settings.muted = Audio.toggleMute(); }
    gamepadMenuNav();

    // Number keys pick a level-up card.
    if (state === 'levelup') {
      for (let i = 1; i <= 4; i++) {
        if (input.pressed(String(i))) {
          const cards = document.querySelectorAll('#overlay .card');
          if (cards[i - 1]) cards[i - 1].click();
        }
      }
    }

    if (state === 'playing') {
      acc += dt;
      let steps = 0;
      while (acc >= STEP && steps < 5) {
        game.update(STEP);
        acc -= STEP; steps++;
        if (state !== 'playing') { acc = 0; break; }
        if (game.pendingLevels > 0 && game.state === 'playing') { openLevelUp(); acc = 0; break; }
        if (game.pendingUltra && game.state === 'playing') { openUltra(); acc = 0; break; }
        if (game.pendingChests > 0 && game.state === 'playing') { openChest(); acc = 0; break; }
      }
      if (steps >= 5) acc = 0; // drop backlog after a spike
      tutorial.update(dt);
    } else {
      acc = 0;
    }
    tutorial.setVisible(state === 'playing');
    // The touch pause button is only meaningful in a run — it was un-hidden
    // once at boot and then drawn over every menu, where it does nothing.
    if (touchPause && input.isTouch) {
      touchPause.classList.toggle('hidden', !(state === 'playing' || state === 'paused'));
    }

    updateMusic();

    if (state === 'title' || state === 'select') {
      // Animated key-art vista behind the menus.
      menuT += dt;
      renderTitleScene(ctx, camera.w, camera.h, menuT);
    } else if (game.player) {
      game.render();
      hud.update(game);
    } else {
      ctx.fillStyle = '#06060d';
      ctx.fillRect(0, 0, camera.w, camera.h);
    }
  } catch (err) {
    showFatal(err);
    return;
  }
  requestAnimationFrame(frame);
}

// ------------------------------------------------------------- error handling
function showFatal(err) {
  crashed = true;
  console.error('NEONFANG crashed:', err);
  try {
    const overlay = document.getElementById('overlay');
    overlay.classList.add('show');
    overlay.innerHTML = `
      <div class="panel" style="text-align:center;max-width:440px">
        <div class="title" style="font-size:30px">OOPS</div>
        <p class="hint">Something went wrong and the game had to stop.</p>
        <pre style="font-size:11px;color:#ff8ac0;text-align:left;overflow:auto;max-height:120px;background:#180a12;padding:8px;border-radius:6px">${String(err && err.stack || err).slice(0, 500)}</pre>
        <div class="btn-row"><button class="btn primary" onclick="location.reload()">↻ Reload</button></div>
      </div>`;
  } catch { /* nothing more we can do */ }
}
window.addEventListener('error', (e) => { if (!crashed) showFatal(e.error || e.message); });
window.addEventListener('unhandledrejection', (e) => { if (!crashed) showFatal(e.reason); });

// ---------------------------------------------------- mobile pause button
const touchPause = document.getElementById('btn-pause-touch');
if (touchPause) {
  touchPause.addEventListener('click', () => { if (state === 'playing' || state === 'paused') togglePause(); });
}

// -------------------------------------------------------------------- boot
async function boot() {
  resize();
  Platform.init();   // async; native features light up when ready, web is a no-op
  // Smoother defaults on phones (only when the player hasn't chosen yet).
  if (input.isTouch && (!Save.data.settings || Save.data.settings.particles === undefined)) {
    Settings.particles = 'low';
  }
  Audio.setVolume(Settings.volume);
  Audio.setMuted(Settings.muted);
  Audio.setMusicEnabled(Settings.music);

  screens.loading(0);
  loadSprites((pct) => screens.setLoading(pct));
  await spritesReady;

  goTitle();
  requestAnimationFrame(frame);

  // Surface save-data problems to the player — a quarantined or unwritable
  // save silently noted only on the console is indistinguishable from a wipe.
  const ss = Save.status();
  if (ss.load === 'corrupt') {
    pushToast('⚠️', '<b>Save data could not be read</b><br>Starting fresh — the old data is kept for recovery.');
  } else if (ss.load === 'future') {
    pushToast('⚠️', '<b>Save is from a newer version</b><br>Loaded what this version understands — the original is kept safe.');
  }
  if (ss.write) {
    pushToast('⚠️', '<b>Progress can’t be saved</b><br>Device storage is full or blocked.');
  }
  // A round-trip through import lands here after the reload — confirm it took.
  try {
    if (sessionStorage.getItem('neonfang.import.ok')) {
      sessionStorage.removeItem('neonfang.import.ok');
      pushToast('💾', '<b>Save imported</b><br>Your progress has been restored.');
    }
  } catch { /* sessionStorage blocked — nothing to confirm */ }

  // Mid-session save failures (quota filling up DURING play, not just at
  // boot). One toast per session, ever — the save layer fires the hook only on
  // a healthy→failing transition and this flag stops repeats even then. The
  // boot toast above already covers a session that starts broken.
  let saveFailWarned = !!ss.write;
  Save.onWriteFailure = () => {
    if (saveFailWarned) return;
    saveFailWarned = true;
    const at = Save.status().lastWriteAt;
    const since = at
      ? 'since ' + new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : 'this session';
    pushToast('⚠️', `<b>Saving isn’t working</b><br>Progress ${since} may be lost — try exporting your save from Settings.`, 'danger');
  };

  // Register the service worker for offline play (production nicety).
  // Skipped in the single-file standalone build (no sw.js to fetch).
  if (!window.__NEON_STANDALONE && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('./sw.js').catch(() => { /* offline optional */ });
    });
  }
}
boot();
